// Componente de seleção de datas da viagem — Ida + Volta + toggle.
//
// Visual (estilo Latam/Decolar):
//
//   ┌────────────────────┬─────────────────────┐
//   │ Ida                │ Volta               │
//   │ Dom. 3 mai. 2026   │ Ter. 5 mai. 2026    │
//   └────────────────────┴─────────────────────┘
//   ⊙ Ainda não defini a data
//
// Click em qualquer field abre popup com calendário 2 meses lado-a-lado +
// botões Apagar/Aplicar no rodapé. Toggle "Ainda não defini" desabilita os
// fields e marca state.datesUndefined = true.
//
// API:
//   mountTripDateRangeField(container, {
//     defaultStart, defaultEnd, defaultUndefined,
//     onChange(start, end, undefined),
//     allowUndefined = true,
//   })
//   → retorna handle { setValues, destroy }

import flatpickr from 'flatpickr';
import { Portuguese } from 'flatpickr/dist/l10n/pt.js';

const WEEKDAY_SHORT_PT = ['Dom.', 'Seg.', 'Ter.', 'Qua.', 'Qui.', 'Sex.', 'Sáb.'];
const MONTH_SHORT_PT = ['jan.', 'fev.', 'mar.', 'abr.', 'mai.', 'jun.', 'jul.', 'ago.', 'set.', 'out.', 'nov.', 'dez.'];

// Locale pt-BR custom — o `Portuguese` do flatpickr é pt-PT e NÃO define
// `firstDayOfWeek`, então cai no default do flatpickr. Pra evitar
// desalinhamento entre o cabeçalho do calendar e a coluna onde os dias caem
// (ex.: dia 13/05/2026 ficar visualmente sob "Seg" em vez de "Qua"),
// override explícito com firstDayOfWeek=0 (Domingo, convenção pt-BR) e
// nomes garantidamente em pt-BR.
const PT_BR_LOCALE = {
  ...Portuguese,
  firstDayOfWeek: 0,
  weekdays: {
    shorthand: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
    longhand:  ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'],
  },
  months: {
    shorthand: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
    longhand:  ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
  },
  rangeSeparator: ' até ',
  time_24hr: true,
};

export function mountTripDateRangeField(container, {
  defaultStart, defaultEnd, defaultUndefined = false,
  allowUndefined = true,
  onChange,
} = {}) {
  if (!container) return null;

  // Estado local que reflete o que está aplicado (não o que está sendo
  // editado no popup — só atualiza no clique de Aplicar).
  const STATE = {
    start: defaultStart || null,
    end: defaultEnd || null,
    undef: defaultUndefined,
  };

  // ─── Render do bloco visual ────────────────────────────────
  container.classList.add('tdr-root');
  container.innerHTML = `
    <div class="tdr-fields" data-slot="fields">
      <button class="tdr-field" type="button" data-which="start" aria-label="Selecionar data de ida">
        <span class="tdr-field-label">Ida</span>
        <span class="tdr-field-value" data-slot="start-display">Quando?</span>
      </button>
      <span class="tdr-divider" aria-hidden="true"></span>
      <button class="tdr-field" type="button" data-which="end" aria-label="Selecionar data de volta">
        <span class="tdr-field-label">Volta</span>
        <span class="tdr-field-value" data-slot="end-display">Quando?</span>
      </button>
    </div>
    ${allowUndefined ? `
      <label class="tdr-toggle">
        <input type="checkbox" data-slot="undef-toggle" ${STATE.undef ? 'checked' : ''}/>
        <span class="tdr-toggle-track"><span class="tdr-toggle-thumb"></span></span>
        <span class="tdr-toggle-label">Ainda não defini a data</span>
      </label>
    ` : ''}

    <input type="text" data-slot="anchor" tabindex="-1" aria-hidden="true"
      style="position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;left:0;top:0;"/>
  `;

  const els = {
    fields:       container.querySelector('[data-slot="fields"]'),
    startDisplay: container.querySelector('[data-slot="start-display"]'),
    endDisplay:   container.querySelector('[data-slot="end-display"]'),
    anchor:       container.querySelector('[data-slot="anchor"]'),
    undefToggle:  container.querySelector('[data-slot="undef-toggle"]'),
  };

  // ─── Flatpickr ─────────────────────────────────────────────
  // Nº de meses é RESPONSIVO: 2 lado a lado no desktop, 1 só no
  // celular/tablet estreito. 2 meses ocupam ~615px — mais largo que a
  // maioria dos celulares; o layout lado-a-lado quebrava (os dias de um
  // mês sobrepunham o outro). Abaixo de TWO_MONTH_MIN_WIDTH mostramos 1
  // mês, que cabe em qualquer viewport. O valor é reavaliado a cada
  // abertura do picker (ver override de `fp.open`), então girar o
  // dispositivo / redimensionar a janela entre aberturas é respeitado.
  // `disableMobile: true` mantém o picker JS em vez do input nativo,
  // necessário pra manter o look-and-feel consistente.
  // NOTA: NÃO usamos `appendTo` — deixamos o calendar ir pro <body> (default),
  // pra que o positioning absoluto em px (calculado pelo Flatpickr a partir
  // do `positionElement`) funcione corretamente. Quando appendTo é o container,
  // o calendar entra como child com layout estático e cai no fim do modal.
  //
  // Setinhas (`prevArrow`/`nextArrow`) são as nativas do flatpickr.
  // O ano é exibido inline com o nome do mês ("Maio 2026") via o template
  // do flatpickr — sem year picker, sem input numérico clicável.
  const TWO_MONTH_MIN_WIDTH = 768;
  const desiredMonths = () => (window.innerWidth >= TWO_MONTH_MIN_WIDTH ? 2 : 1);

  const fp = flatpickr(els.anchor, {
    locale: PT_BR_LOCALE,
    mode: 'range',
    showMonths: desiredMonths(),
    inline: false,
    static: false,
    closeOnSelect: false,
    dateFormat: 'Y-m-d',
    altInput: false,
    disableMobile: true,
    defaultDate: [STATE.start, STATE.end].filter(Boolean),
    positionElement: els.fields,
    prevArrow: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>',
    nextArrow: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>',
  });

  // Tag custom no container — permite estilizar o calendar (fundo escuro,
  // borda, sombra) tanto no modo 1 mês quanto 2 meses. O CSS antigo só
  // cobria `.multiMonth`; sem essa classe o calendar de 1 mês cairia no
  // tema branco default do flatpickr.
  fp.calendarContainer?.classList.add('tdr-calendar');

  // ─── Footer custom (Apagar/Aplicar) ────────────────────────
  // Injeta no calendarContainer depois do Flatpickr render
  function ensureFooter() {
    if (!fp.calendarContainer) return;
    let footer = fp.calendarContainer.querySelector('.tdr-footer');
    if (!footer) {
      footer = document.createElement('div');
      footer.className = 'tdr-footer';
      footer.innerHTML = `
        <button type="button" class="tdr-foot-clear" data-fp-action="clear">Apagar</button>
        <button type="button" class="tdr-foot-apply" data-fp-action="apply">Aplicar</button>
      `;
      footer.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-fp-action]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        if (btn.dataset.fpAction === 'clear') {
          fp.clear();
          return;
        }
        if (btn.dataset.fpAction === 'apply') {
          const [s, e2] = fp.selectedDates;
          STATE.start = isoFromDate(s);
          STATE.end = isoFromDate(e2);
          renderDisplay();
          onChange?.(STATE.start, STATE.end, STATE.undef);
          fp.close();
        }
      });
    }
    // Mantém o footer como último filho — fp.set('showMonths') re-anexa
    // monthNav/rContainer e empurraria o footer pra cima dos dias.
    fp.calendarContainer.appendChild(footer);
  }

  // ─── Header: ano inline com nome do mês ("Maio 2026") ───
  // Flatpickr default renderiza ".flatpickr-current-month" contendo
  // "<MesSelect.cur-month> <numInputWrapper><input.cur-year>...</numInputWrapper>".
  // O numInputWrapper continua escondido via CSS, mas o ano em si DEVE ficar
  // visível. Injetamos um span ".tdr-year-display" no pai .flatpickr-current-month,
  // logo após o .cur-month, e CALCULAMOS o ano a partir do estado interno do
  // flatpickr (fp.currentYear + paneIndex offset), em vez de ler do input.
  //
  // Bug fix 2026-05-18: antes a gente lia `yearInput.value` e usava
  // MutationObserver no atributo `value` — não funcionava porque flatpickr
  // muda `.value` (propriedade), não o atributo. Resultado: ao virar de
  // Dezembro 2026 pra Janeiro, o 2º pane mostrava "Janeiro 2026" em vez
  // de "Janeiro 2027". Agora calculamos: pane 0 = currentMonth, pane 1 =
  // currentMonth+1 (wrap ano se passar de Dezembro).
  function flattenYearHeader() {
    if (!fp.calendarContainer) return;
    const headers = fp.calendarContainer.querySelectorAll('.flatpickr-current-month');
    headers.forEach((header, paneIndex) => {
      // Calcula ano do mês deste pane a partir do estado do flatpickr.
      let year = fp.currentYear;
      let month = (fp.currentMonth || 0) + paneIndex;
      while (month > 11) { month -= 12; year += 1; }
      while (month < 0)  { month += 12; year -= 1; }

      const monthEl = header.querySelector('.cur-month');
      let span = header.querySelector('.tdr-year-display');
      if (!span) {
        span = document.createElement('span');
        span.className = 'tdr-year-display';
        if (monthEl && monthEl.nextSibling) {
          header.insertBefore(span, monthEl.nextSibling);
        } else {
          header.appendChild(span);
        }
      }
      span.textContent = String(year);
    });
  }

  // Hook into Flatpickr events
  const origOpen = fp.open.bind(fp);
  fp.open = (...args) => {
    // Reavalia 1-vs-2 meses pela largura atual da janela antes de abrir.
    const want = desiredMonths();
    if (fp.config.showMonths !== want) fp.set('showMonths', want);
    origOpen(...args);
    ensureFooter();
    flattenYearHeader();
  };
  // Re-aplica o flatten quando o mês/ano muda (flatpickr re-renderiza o header
  // em algumas transições). Flatpickr armazena hooks como arrays; .push() basta.
  // Tipagem defensiva caso a versão expor não-array:
  const _pushHook = (key) => {
    const cur = fp.config[key];
    if (Array.isArray(cur)) cur.push(flattenYearHeader);
    else fp.config[key] = [flattenYearHeader, ...(cur ? [cur] : [])];
  };
  _pushHook('onMonthChange');
  _pushHook('onYearChange');

  // ─── Display ───────────────────────────────────────────────
  function renderDisplay() {
    const startTxt = STATE.start ? formatBr(parseIso(STATE.start)) : 'Quando?';
    const endTxt   = STATE.end   ? formatBr(parseIso(STATE.end))   : 'Quando?';
    els.startDisplay.textContent = startTxt;
    els.startDisplay.classList.toggle('is-empty', !STATE.start);
    els.endDisplay.textContent = endTxt;
    els.endDisplay.classList.toggle('is-empty', !STATE.end);
    els.fields.classList.toggle('is-disabled', STATE.undef);
  }

  // ─── Click handlers ───────────────────────────────────────
  els.fields.addEventListener('click', (e) => {
    if (STATE.undef) return;
    e.preventDefault();
    fp.open();
  });

  if (els.undefToggle) {
    els.undefToggle.addEventListener('change', () => {
      STATE.undef = !!els.undefToggle.checked;
      if (STATE.undef) {
        // Limpa datas e fecha picker
        fp.clear();
        STATE.start = null;
        STATE.end = null;
        fp.close();
      }
      renderDisplay();
      onChange?.(STATE.start, STATE.end, STATE.undef);
    });
  }

  // Render inicial
  renderDisplay();

  return {
    setValues({ start, end, undef } = {}) {
      STATE.start = start || null;
      STATE.end = end || null;
      STATE.undef = !!undef;
      if (els.undefToggle) els.undefToggle.checked = STATE.undef;
      const init = [STATE.start, STATE.end].filter(Boolean);
      if (init.length) fp.setDate(init, false);
      else fp.clear(false);
      renderDisplay();
    },
    getValues() {
      return { start: STATE.start, end: STATE.end, undef: STATE.undef };
    },
    destroy() {
      fp.destroy();
    },
  };
}

// ---------- helpers ----------

function formatBr(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const wd = WEEKDAY_SHORT_PT[d.getDay()];
  const day = d.getDate();
  const month = MONTH_SHORT_PT[d.getMonth()];
  const year = d.getFullYear();
  return `${wd} ${day} ${month} ${year}`;
}

function parseIso(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function isoFromDate(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}
